import { AddEventListernerResult, Loading, useEventListenersRef } from "@kalyzee/kast-web-sdk/web-components";
import React, { useContext, useRef, useState } from "react";
import { Location, NavigateOptions, Params, useLocation, useNavigate } from "react-router-dom";
import { useLogger } from "../../helpers/logger";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../helpers/navigate";
import { useAppDispatch } from "../../hooks/app";
import { useMode } from "../../store/session/hooks";
import { SessionMode, setMode } from "../../store/session/slices";
import SettingsPopup, { SettingsPopupRef } from "../utils/SettingsPopup";

export enum PageMasterMode {
  LOGIN = "login",
  LOGOUT = "logout",
  FORBIDDEN = "forbidden",
  EMPTY = "empty",
  LIST = "list",
  MEETING = "meeting",
  MEETING_CALL = "meeting-call",
  MEETING_DASHBOARD = "meeting-dashboard",
  MEETING_HIGHLIGHT = "meeting-highlight",
  MEETING_SURVEY = "meeting-survey",
  MEETING_GROUP = "meeting-group",
  MEETING_PEDAGOGY = "meeting-pedagogy",
  MEETING_COMMUNICATION = "meeting-communication",
  MEETING_INTERVENTION = "meeting-intervention",
  MEETING_INTERVENTION_TALK_TO_EXPERT = "meeting-intervention-talk-to-expert",
  MEETING_INTERVENTION_DASHBOARD = "meeting-intervention-dashboard",
  MEETING_INTERVENTION_HIGHLIGHT = "meeting-intervention-highlight",
  MEETING_INTERVENTION_GROUP = "meeting-intervention-group",
  MEETING_INTERVENTION_SURVEY = "meeting-intervention-survey",
  VOD = "vod",
  DASHBOARD = 'dashboard',
  OVERVIEW = 'overview',
  WAITING_ROOM = "waiting-room",
}

export enum PageSlaveMode {
  MEETING = "meeting",
}

export const PageMap = {
  [SessionMode.MASTER]: PageMasterMode,
  [SessionMode.SLAVE]: PageSlaveMode,
} as const;
export interface PageMap {
  [SessionMode.MASTER]: PageMasterMode;
  [SessionMode.SLAVE]: PageSlaveMode;
}

export const PAGE_PATHS: { [k in SessionMode]: { [key in PageMap[k]]: string } } = {
  [SessionMode.MASTER]: {
    [PageMasterMode.LOGIN]: "/login",
    [PageMasterMode.LOGOUT]: "/logout",
    [PageMasterMode.FORBIDDEN]: "/forbidden",
    [PageMasterMode.EMPTY]: "/empty",
    [PageMasterMode.LIST]: "/list",
    [PageMasterMode.MEETING]: "/meeting/:id",
    [PageMasterMode.MEETING_DASHBOARD]: "/meeting/:id/dashboard",
    [PageMasterMode.MEETING_HIGHLIGHT]: "/meeting/:id/highlight",
    [PageMasterMode.MEETING_CALL]: "/meeting/:id/call",
    [PageMasterMode.MEETING_SURVEY]: "/meeting/:id/survey",
    [PageMasterMode.MEETING_GROUP]: "/meeting/:id/group",
    [PageMasterMode.MEETING_PEDAGOGY]: "/meeting/:id/pedagogy",
    [PageMasterMode.MEETING_COMMUNICATION]: "/meeting/:id/communication",
    [PageMasterMode.MEETING_INTERVENTION]: "/meeting/:id/intervention",
    [PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT]: "/meeting/:id/intervention/expert",
    [PageMasterMode.MEETING_INTERVENTION_DASHBOARD]: "/meeting/:id/intervention/dashboard",
    [PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT]: "/meeting/:id/intervention/highlight",
    [PageMasterMode.MEETING_INTERVENTION_GROUP]: "/meeting/:id/intervention/group",
    [PageMasterMode.MEETING_INTERVENTION_SURVEY]: "/meeting/:id/intervention/survey",
    [PageMasterMode.VOD]: "/vod/:id",
    [PageMasterMode.DASHBOARD]: "/dashboard",
    [PageMasterMode.OVERVIEW]: "/overview",
    [PageMasterMode.WAITING_ROOM]: "/waiting-room",
  },
  [SessionMode.SLAVE]: {
    [PageSlaveMode.MEETING]: "/meeting",
  },
};

export interface PageContextEventListenerMap<M extends SessionMode> {
  onBeforeGoto: <T extends PageMap[M]>(path: string, page: T, params?: any, options?: PageOptions) => void;
}

export interface PageOptions extends NavigateOptions {
  mode?: SessionMode;
  queries?: { [key: string]: string | undefined };
  new?: boolean; // new tab
}

export interface PageContextState<M extends SessionMode> {
  readonly from: {
    readonly location?: Location;
    readonly params?: Params;
    readonly page?: PageMap[M];
  };
  readonly location: Location;
  readonly page?: PageMap[M];
  readonly searchParams: URLSearchParams;
  readonly mode: M;
  readonly params?: Params;
  readonly isLoading: boolean;
  update: <T extends SessionMode>(mode: T, page: PageMap[T], params?: any) => void;
  goTo: <T extends PageMap[M]>(page: T, params?: any, options?: PageOptions) => boolean;
  canGoPreviousPage: () => boolean;
  goToPrevious: <T extends PageMap[M]>(defaultPage?: T, params?: any, options?: PageOptions) => boolean;
  openSettingsPopup: () => void;
  getPreviousPage: () => PageMap[M] | undefined;
  setLoading: (value: boolean, message?: string) => void;
  addEventListener: <K extends keyof PageContextEventListenerMap<M>>(
    key: K,
    callback: PageContextEventListenerMap<M>[K]
  ) => AddEventListernerResult<PageContextEventListenerMap<M>, K>;
  removeEventListener: <K extends keyof PageContextEventListenerMap<M>>(key: K, callback: PageContextEventListenerMap<M>[K]) => void;
}
export const PageContext = React.createContext<PageContextState<any> | undefined>(undefined);

export const getPageUrl = <M extends SessionMode, T extends PageMap[M]>(mode: M, page: T, params?: any, options?: PageOptions): string => {
  let path = (PAGE_PATHS[mode] as any)[page];
  const queries = options?.queries ?? {};
  if (mode === SessionMode.SLAVE) queries[getGlobalQueryLabel(NavigateGlobalQuery.SLAVE)] = undefined;
  if (params) {
    const keys = Object.keys(params);
    keys.forEach((k) => {
      const regex = new RegExp(`(/:${k})(/|$)`);
      const param = (params as any)[k];
      path = path.replace(regex, `/${param}$2`);
    });
  }
  if (queries) {
    const keys = Object.keys(queries);
    if (keys.length) {
      let urlQueries = "?";
      keys.forEach((k, i) => {
        const query: string | undefined = queries[k];
        if (i !== 0) urlQueries = `${urlQueries}&`;
        if (query) urlQueries = `${urlQueries}${k}=${query}`;
        else urlQueries = `${urlQueries}${k}`;
      });
      path = `${path}${urlQueries}`;
    }
  }
  return path;
};

export const PageProvider = <M extends SessionMode>({ children }: { children: React.ReactNode }) => {
  const eventListenersRef = useEventListenersRef<keyof PageContextEventListenerMap<M>, PageContextEventListenerMap<M>>();
  const location = useLocation();
  const previousLocation = useRef<Location>();
  const previousParams = useRef<Readonly<Params<string>>>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mode = useMode();
  const [page, setPage] = useState<PageMap[M] | undefined>();
  const previousPageRef = useRef<PageMap[M]>();
  const paramsRef = useRef<Params>();
  const settingsPopupRef = useRef<SettingsPopupRef>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>();
  const logger = useLogger();

  const canGoPreviousPage = () => {
    const nextRoute = state?.from?.location;
    const nextPath = nextRoute?.pathname;
    if (!nextPath || nextRoute === state?.location) return false;
    return true;
  };

  const getPreviousPage = () => {
    return state?.from?.page;
  };

  const goTo = <T extends PageMap[M]>(page: T, params?: any, options?: PageOptions) => {
    if (!mode) return false;
    const path = getPageUrl(mode, page, params, options);
    if (options?.new) {
      const newWindow = window.open(path, '_blank', 'noopener,noreferrer')
      if (newWindow) newWindow.opener = null
      return newWindow ? true : false;
    }
    eventListenersRef.current.triggerEvent("onBeforeGoto", path, page, params, options);
    logger.log("[PAGE] Go to : ", page, " params : ", params, " path : ", path);
    previousLocation.current = state.location;
    previousParams.current = state.params;
    previousPageRef.current = state.page;
    navigate(path, options);
    if (options?.mode && options.mode !== mode) {
      dispatch(setMode(options.mode));
    }
    setPage(page);
    return true;
  };

  const state: PageContextState<M> = {
    from: {
      location: previousLocation.current,
      params: previousParams.current,
      page: previousPageRef.current,
    },
    location,
    page: page,
    searchParams: new URLSearchParams(location?.search),
    mode: mode as any,
    get params() { return paramsRef.current },
    isLoading: loading,
    update: <T extends SessionMode>(mode: T, page: PageMap[T], params?: Params) => {
      dispatch(setMode(mode));
      setPage(page as any);
      paramsRef.current = params;
    },
    goTo,
    canGoPreviousPage,
    goToPrevious: <T extends PageMap[M]>(defaultPage?: T, params?: any, options?: PageOptions) => {
      if (!mode) return false;
      const nextRoute = state?.from?.location;
      const nextPath = nextRoute?.pathname;
      const previousPage = previousPageRef.current;
      logger.log("[PAGE] Go to previous : ", previousPage, nextPath);
      previousLocation.current = state.location;
      previousParams.current = state.params;
      previousPageRef.current = state.page;
      if (!nextPath || nextRoute === state?.location) {
        if (defaultPage) {
          goTo(defaultPage, params, options);
        } else {
          const page = (PageMap[mode] as any).LOGIN;
          setPage(page);
          navigate((PAGE_PATHS[mode] as any)[page]);
        }

      } else {
        setPage(previousPage);
        navigate(nextPath);
      }
      return true;
    },
    openSettingsPopup: () => {
      return settingsPopupRef.current?.openPopup();
    },
    setLoading: (value, message) => {
      setLoading(value);
      if (value) setLoadingMessage(message);
      else setLoadingMessage(undefined);
    },
    getPreviousPage,
    addEventListener: <K extends keyof PageContextEventListenerMap<M>>(key: K, callback: PageContextEventListenerMap<M>[K]) =>
      eventListenersRef.current?.addEventListener(key, callback),
    removeEventListener: <K extends keyof PageContextEventListenerMap<M>>(key: K, callback: PageContextEventListenerMap<M>[K]) =>
      eventListenersRef.current?.removeEventListener(key, callback),
  };

  return (
    <PageContext.Provider value={state}>
      {children}
      <SettingsPopup ref={settingsPopupRef} />
      {loading ? <Loading fullscreen message={loadingMessage} /> : null}
    </PageContext.Provider>
  );
};

export const usePageContext = <M extends SessionMode = any>(): PageContextState<M> | undefined => {
  return useContext(PageContext);
};

export const usePageContextRef = <M extends SessionMode = any>(): { current: PageContextState<M> | undefined } => {
  const context = usePageContext();
  const ref = useRef<PageContextState<M> | undefined>(context);
  ref.current = context;
  return ref;
};

export const pageContextIsSessionMode = <M extends SessionMode>(mode: M, context: PageContextState<any>): context is PageContextState<M> => {
  return context?.mode === mode;
};
